import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { RegistrationForm } from '../components/forms/RegistrationForm';
import { Background } from '../components/layout/Background';
import { BackgroundLeftText } from '../components/layout/BackgroundLeftText';
import { useAppDispatch } from '../hooks/redux';
import { resetAuthState } from '../store/slices/authSlice';
import { showToast } from '../store/slices/toastSlice';
import { PATHS } from '../constants/Paths.constants';
import { validateInvitation } from '../services/TeamService';
import Loader from '../components/ui/Loader/Loader';
import { featureFlags } from '../config/featureFlags';
import { PlatformRole } from '../types/Team.types';

const DEFAULT_HEADING = 'Create your account';

const headingFor = (role?: PlatformRole, organizationName?: string) => {
  if (!organizationName) return DEFAULT_HEADING;

  return role === 'ADMIN'
    ? `You’ve been invited to administer ${organizationName}`
    : `You’ve been invited to join ${organizationName}`;
};

export function RegistrationPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tokenFromUrl = searchParams.get('token') || '';
  const emailFromUrl = searchParams.get('email') || '';
  const isInvitationFlow = Boolean(tokenFromUrl && emailFromUrl);

  const [validatingToken, setValidatingToken] = useState(true);
  const [heading, setHeading] = useState(DEFAULT_HEADING);

  useEffect(() => {
    dispatch(resetAuthState());
  }, [dispatch]);

  useEffect(() => {
    if (!tokenFromUrl || !emailFromUrl) {
      setValidatingToken(false);
      return;
    }

    const rejectInvitation = (errorCode?: string) => {
      if (errorCode === 'TEAM_INVITATION_ALREADY_ACCEPTED') {
        dispatch(
          showToast({ message: 'Invitation already accepted', type: 'error' })
        );
        navigate(PATHS.LOGIN, { replace: true });
        return;
      }

      navigate(PATHS.EXPIRED_LINK, {
        replace: true,
        state: { from: 'invitation' }
      });
    };

    const validateInvitationToken = async () => {
      setValidatingToken(true);

      try {
        const { data } = await validateInvitation(tokenFromUrl);

        if (!data.valid) {
          rejectInvitation(data.errorCode);
          return;
        }

        setHeading(headingFor(data.role, data.organizationName));
        setValidatingToken(false);
      } catch (error: any) {
        rejectInvitation(error?.response?.data?.errorCode);
      }
    };

    validateInvitationToken();
  }, [tokenFromUrl, emailFromUrl, navigate, dispatch]);

  // Accepting an invitation must work even when open sign-up is disabled.
  if (!isInvitationFlow && !featureFlags.signUpEnabled) {
    return <Navigate to={PATHS.LOGIN} replace />;
  }

  return (
    <div className="min-w-screen min-h-screen relative flex flex-col md:flex-row justify-between items-center">
      <Background />
      {validatingToken ? (
        <div className="flex w-screen h-screen justify-center items-center">
          <Loader />
        </div>
      ) : (
        <>
          <BackgroundLeftText />
          <div className="w-full md:w-1/2 p-4 flex flex-col items-center justify-center">
            <div className="w-full max-w-[458px] py-8 px-10 rounded-2xl bg-white shadow-sm border border-gray-200">
              <p className="text-2xl text-purple-900 font-bold text-left mb-6">
                {heading}
              </p>
              <RegistrationForm
                invitation={isInvitationFlow}
                invitationToken={tokenFromUrl}
                invitationEmail={emailFromUrl}
              />
              <p className="mt-6 text-xs font-normal text-gray-500 text-left tracking-wide">
                By clicking ‘Create Account’ you agree to accept our {''}
                <a href={`${PATHS.TERMSANDPRIVACY}?tab=terms`}>
                  <span className="underline-custom">Terms of</span> <br />
                  <span className="underline-custom">Service</span>
                </a>
                {''} and {''}
                <a href={`${PATHS.TERMSANDPRIVACY}?tab=policy`}>
                  <span className="underline-custom">Privacy Policy</span>.
                </a>{' '}
                {''}
                You can always access these <br /> documents later in the app
                main menu.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
